import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/useEntity';
import { fmt } from '../lib/formatters';
import { useAuth } from '../lib/useAuth';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Spinner } from '../components/ui/Spinner';
import { toast } from '../lib/toast';

/**
 * Centro de mando global (docs/rediseno-ux/dashboard-propuesta.html, fase 2).
 * Pantalla de inicio de la app, con las cuatro bandas del Role Center de
 * Business Central, de arriba abajo:
 *
 *   1. titulares — la frase que resume el negocio hoy, rotando;
 *   2. avisos    — lo que requiere acción, cada número abre su lista;
 *   3. pedidos y tareas — lo que está en marcha y lo que toca hacer;
 *   4. un bloque por cliente con sus KPIs y el enlace a su análisis.
 *
 * Datos: GET /api/inicio-global (una sola llamada).
 */

type Titular = { categoria: string; texto: string; enlace: string };
type Aviso = { key: string; titulo: string; valor: string; detalle: string; tono: 'bad' | 'warn' | 'neutral' | 'good'; enlace: string; cliente: string | null };
type Pedido = {
  id: string; cliente_id: string; cliente_codigo: string; numero: string | null; estado: string;
  fecha_pedido: string | null; eta: string | null; uds: number; entregadas: number; importe: number; lineas: number;
};
type Tarea = {
  id: string; titulo: string; detalle: string | null; cliente_id: string | null; cliente_codigo: string | null;
  origen: 'manual' | 'regla'; regla_key: string | null; prioridad: 'alta' | 'media' | 'baja'; vence: string | null;
  enlace: string | null; hecha_at: string | null; silenciada_at: string | null; created_at: string;
};
type Cobertura = {
  ventas_approx_hasta: string | null; coste_entrada_hasta: string | null; stock_hasta: string | null;
  sellout_unidad: 'mes' | 'semana' | null; sellout_hasta: string | null;
  sellin_erp_hasta: string | null; sellin_pedidos_hasta: string | null;
};
type KpiCliente = {
  cliente: { id: string; codigo: string; nombre: string };
  fuente_sellin: 'erp' | 'pedidos' | null;
  sellin: {
    periodo: string; eur: number; uds: number; operaciones: number; parcial: boolean;
    eur_py: number; uds_py: number; delta_eur: number | null; delta_uds: number | null; comparado_con: string;
    asp: number | null; asp_py: number | null; delta_asp: number | null;
  } | null;
  sellout: { mes: string; etiqueta: string; uds: number; uds_py: number | null; delta: number | null; provisional: boolean; unidad: 'mes' | 'semana' } | null;
  sellout_en_curso: { mes: string; etiqueta: string; uds: number } | null;
  serie12: { mes: string; eur: number; uds: number; so_uds: number | null }[];
  rotura: { n: number; sin_stock: number; unidad: 'meses' | 'semanas'; umbral: number; top: { id: string; sku: string; nombre: string; cobertura: number | null }[] };
  cotizaciones: { sin_respuesta: number; sin_enviar: number; aceptadas_30d: number; mas_antigua: string | null };
  pedidos: { abiertos: number; importe: number; eta_vencida: number; sin_eta: number };
  codigos_sin_enlazar: number;
  cobertura: Cobertura;
  top_producto: { sku: string | null; nombre: string | null; uds: number; eur: number } | null;
};
type InicioGlobalData = {
  fecha: string;
  titulares: Titular[];
  avisos: Aviso[];
  pedidos: Pedido[];
  tareas: Tarea[];
  clientes: KpiCliente[];
  sync: { vivas: number; creadas: number; resueltas: number };
};

const TONO_BORDE: Record<Aviso['tono'], string> = {
  bad: 'border-bad-300 hover:border-bad-500',
  warn: 'border-warn-300 hover:border-warn-500',
  neutral: 'border-slate-200 hover:border-accent-300',
  good: 'border-ok-300 hover:border-ok-500',
};
const TONO_TEXTO: Record<Aviso['tono'], string> = {
  bad: 'text-bad-600', warn: 'text-warn-600', neutral: 'text-slate-900', good: 'text-ok-600',
};

function saludo(): string {
  const h = new Date().getHours();
  return h < 13 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches';
}

/** Pinta los `**tramos**` del titular en negrita, sin meter HTML en el DOM. */
function conNegritas(texto: string): ReactNode[] {
  return texto.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((tr, i) =>
    tr.startsWith('**') && tr.endsWith('**')
      ? <b key={i} className="font-display font-extrabold text-slate-900">{tr.slice(2, -2)}</b>
      : <span key={i}>{tr}</span>,
  );
}

/** Variación con el signo y el color del holding (verde sube, rojo baja). */
function Delta({ v, invertido = false, className = '' }: { v: number | null | undefined; invertido?: boolean; className?: string }) {
  if (v == null) return <span className={`text-slate-400 ${className}`}>—</span>;
  const bueno = invertido ? v <= 0 : v >= 0;
  return (
    <span className={`font-semibold tabular-nums ${bueno ? 'text-ok-600' : 'text-bad-600'} ${className}`}>
      {v >= 0 ? '▲' : '▼'} {fmt.number(Math.abs(v))} %
    </span>
  );
}

export function InicioGlobal() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['inicio-global'],
    queryFn: () => api.get<InicioGlobalData>('/inicio-global'),
    staleTime: 60_000,
  });
  const d = q.data;
  const nombre = user?.email?.split('@')[0] ?? '';

  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['inicio-global'] }); };

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{saludo()}, {nombre}</h1>
          <span className="text-sm text-slate-500">{fmt.date(new Date())} · centro de mando</span>
        </div>
        {d && <span className="text-xs text-slate-400">{d.tareas.length} tareas abiertas · {d.pedidos.length} pedidos en curso</span>}
      </div>

      {q.isLoading && <div className="py-10"><Spinner /></div>}
      {q.error && <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudo cargar el centro de mando. {(q.error as Error).message}</p>}

      {d && (
        <div className="space-y-4">
          <Titulares titulares={d.titulares} />
          <Avisos avisos={d.avisos} />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            <div className="xl:col-span-3"><PedidosAbiertos pedidos={d.pedidos} /></div>
            <div className="xl:col-span-2"><Tareas tareas={d.tareas} clientes={d.clientes} onCambio={refrescar} /></div>
          </div>

          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            {d.clientes.map((k) => <BloqueCliente key={k.cliente.id} k={k} />)}
          </div>
        </div>
      )}
    </div>
  );
}

/** Banda 1 — un titular cada vez, rotando cada 9 s, con el resto accesible. */
function Titulares({ titulares }: { titulares: Titular[] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (titulares.length < 2) return;
    const t = window.setInterval(() => setI((x) => (x + 1) % titulares.length), 9000);
    return () => window.clearInterval(t);
  }, [titulares.length]);
  if (!titulares.length) return null;
  const t = titulares[Math.min(i, titulares.length - 1)];
  return (
    <section aria-label="Titulares" className="rounded-lg border border-accent-300 bg-gradient-to-r from-accent-50 to-white px-5 py-4 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-accent-700">{t.categoria}</div>
          <Link to={t.enlace} className="mt-1 block text-[15px] leading-snug text-slate-700 hover:underline">{conNegritas(t.texto)}</Link>
        </div>
        {titulares.length > 1 && (
          <div className="flex shrink-0 gap-1 pt-1" role="tablist" aria-label="Cambiar titular">
            {titulares.map((_, n) => (
              <button key={n} type="button" onClick={() => setI(n)} aria-label={`Titular ${n + 1}`} aria-selected={n === i} role="tab"
                className={`h-1.5 w-1.5 cursor-pointer rounded-full transition ${n === i ? 'bg-accent-600' : 'bg-accent-300 hover:bg-accent-500'}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Banda 2 — cues: un número, su contexto y el enlace que lo resuelve. */
function Avisos({ avisos }: { avisos: Aviso[] }) {
  if (!avisos.length) return null;
  return (
    <section aria-label="Avisos" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      {avisos.map((a) => (
        <Link key={a.key} to={a.enlace} className={`relative block rounded-lg border bg-white px-4 py-3 shadow-sm transition hover:bg-accent-50/40 ${TONO_BORDE[a.tono]}`}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500" title={a.titulo}>{a.titulo}</span>
            {a.cliente && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">{a.cliente}</span>}
          </div>
          <div className={`mt-1 font-display text-2xl font-extrabold leading-tight ${TONO_TEXTO[a.tono]}`}>{a.valor}</div>
          <div className="mt-0.5 truncate text-xs text-slate-500" title={a.detalle}>{a.detalle}</div>
        </Link>
      ))}
    </section>
  );
}

const ESTADO_TONO: Record<string, 'good' | 'warn' | 'bad' | 'neutral' | 'accent'> = {
  PDTE_RESPUESTA: 'neutral', ACEPTADO: 'accent', EN_PRODUCCION: 'warn', EN_CAMINO: 'warn', INCIDENCIA: 'bad',
};

/** Banda 3a — pedidos abiertos de todos los clientes, los más urgentes arriba. */
function PedidosAbiertos({ pedidos }: { pedidos: Pedido[] }) {
  const hoy = new Date().toISOString().slice(0, 10);
  return (
    <section className="h-full rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="font-display text-sm font-bold">Pedidos en curso</h2>
        <span className="text-xs text-slate-500">por fecha de entrega</span>
      </header>
      {pedidos.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">No hay pedidos abiertos.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-semibold">Pedido</th>
                <th className="px-2 py-2 font-semibold">Estado</th>
                <th className="px-2 py-2 text-right font-semibold">Uds</th>
                <th className="px-2 py-2 text-right font-semibold">Importe</th>
                <th className="px-5 py-2 text-right font-semibold">Entrega</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pedidos.map((p) => {
                const vencida = Boolean(p.eta && p.eta < hoy);
                const pendientes = Math.max(0, p.uds - p.entregadas);
                return (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-5 py-2">
                      <Link to={`/${p.cliente_codigo.toLowerCase()}/pedidos/${p.id}`} className="font-medium text-slate-800 hover:underline">
                        {p.numero ?? '(sin número)'}
                      </Link>
                      <span className="ml-2 text-[10px] font-bold text-slate-400">{p.cliente_codigo}</span>
                    </td>
                    <td className="px-2 py-2"><Badge tone={ESTADO_TONO[p.estado] ?? 'neutral'} dot>{p.estado.replace(/_/g, ' ').toLowerCase()}</Badge></td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{fmt.int(pendientes)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-800">{fmt.eur(p.importe)}</td>
                    <td className={`px-5 py-2 text-right tabular-nums ${vencida ? 'font-semibold text-bad-600' : 'text-slate-600'}`}>
                      {p.eta ? fmt.date(p.eta) : <span className="text-warn-600">sin fecha</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Banda 3b — tareas: las de regla se silencian, las manuales se borran. */
function Tareas({ tareas, clientes, onCambio }: { tareas: Tarea[]; clientes: KpiCliente[]; onCambio: () => void }) {
  const [abrirNueva, setAbrirNueva] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [prioridad, setPrioridad] = useState<'alta' | 'media' | 'baja'>('media');
  const [vence, setVence] = useState('');

  const crear = useMutation({
    mutationFn: () => api.post<Tarea>('/tareas', { titulo, cliente_id: clienteId || null, prioridad, vence: vence || null }),
    onSuccess: () => { setTitulo(''); setVence(''); setAbrirNueva(false); toast.success('Tarea añadida'); onCambio(); },
    onError: (e: Error) => toast.error('No se pudo crear la tarea', e.message),
  });
  const marcar = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.patch<Tarea>(`/tareas/${v.id}`, v.body),
    onSuccess: () => onCambio(),
    onError: (e: Error) => toast.error('No se pudo actualizar la tarea', e.message),
  });
  const borrar = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/tareas/${id}`),
    onSuccess: () => { toast.success('Tarea eliminada'); onCambio(); },
    onError: (e: Error) => toast.error('No se pudo eliminar', e.message),
  });

  const hoy = new Date().toISOString().slice(0, 10);
  const prio: Record<Tarea['prioridad'], 'bad' | 'warn' | 'neutral'> = { alta: 'bad', media: 'warn', baja: 'neutral' };

  return (
    <section className="flex h-full flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="font-display text-sm font-bold">Próximas tareas</h2>
        <Button size="sm" variant="ghost" onClick={() => setAbrirNueva((v) => !v)} aria-expanded={abrirNueva}>
          {abrirNueva ? 'Cancelar' : '+ Añadir'}
        </Button>
      </header>

      {abrirNueva && (
        <form className="space-y-2 border-b border-slate-100 bg-slate-50 px-5 py-3"
          onSubmit={(e) => { e.preventDefault(); if (titulo.trim()) crear.mutate(); }}>
          <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="¿Qué hay que hacer?" aria-label="Título de la tarea" autoFocus />
          <div className="flex flex-wrap gap-2">
            <Select value={clienteId} onChange={(e) => setClienteId(e.target.value)} aria-label="Cliente" className="flex-1"
              emptyLabel="Sin cliente" options={clientes.map((c) => ({ value: c.cliente.id, label: c.cliente.codigo }))} />
            <Select value={prioridad} onChange={(e) => setPrioridad(e.target.value as 'alta' | 'media' | 'baja')} aria-label="Prioridad" className="flex-1"
              emptyLabel={null} options={[{ value: 'alta', label: 'Prioridad alta' }, { value: 'media', label: 'Prioridad media' }, { value: 'baja', label: 'Prioridad baja' }]} />
            <Input type="date" value={vence} onChange={(e) => setVence(e.target.value)} aria-label="Vence" className="flex-1" />
          </div>
          <Button type="submit" variant="create" size="sm" disabled={!titulo.trim() || crear.isPending}>Añadir tarea</Button>
        </form>
      )}

      <ul className="flex-1 divide-y divide-slate-100 overflow-y-auto px-5" style={{ maxHeight: '26rem' }}>
        {tareas.map((t) => {
          const vencida = Boolean(t.vence && t.vence < hoy);
          return (
            <li key={t.id} className="flex items-start gap-2 py-2.5 text-sm">
              <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-accent-600"
                checked={false} aria-label={`Marcar «${t.titulo}» como hecha`}
                onChange={() => marcar.mutate({ id: t.id, body: { hecha: true } })} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge tone={prio[t.prioridad]}>{t.prioridad}</Badge>
                  {t.cliente_codigo && <span className="text-[10px] font-bold text-slate-400">{t.cliente_codigo}</span>}
                  {t.origen === 'regla' && <span className="text-[10px] uppercase tracking-wide text-slate-400" title="Generada por una regla del sistema">auto</span>}
                </div>
                {t.enlace
                  ? <Link to={t.enlace} className="mt-0.5 block text-slate-800 hover:underline">{t.titulo}</Link>
                  : <span className="mt-0.5 block text-slate-800">{t.titulo}</span>}
                {t.detalle && <span className="block text-xs text-slate-500">{t.detalle}</span>}
                {t.vence && <span className={`block text-xs ${vencida ? 'font-semibold text-bad-600' : 'text-slate-400'}`}>vence el {fmt.date(t.vence)}</span>}
              </div>
              <button type="button" title={t.origen === 'regla' ? 'Silenciar: vuelve si la condición se repite' : 'Eliminar tarea'}
                aria-label={t.origen === 'regla' ? `Silenciar «${t.titulo}»` : `Eliminar «${t.titulo}»`}
                className="shrink-0 cursor-pointer px-1 text-xs text-slate-400 hover:text-slate-700"
                onClick={() => (t.origen === 'regla' ? marcar.mutate({ id: t.id, body: { silenciada: true } }) : borrar.mutate(t.id))}>
                {t.origen === 'regla' ? '🔕' : '✕'}
              </button>
            </li>
          );
        })}
        {tareas.length === 0 && <li className="py-8 text-center text-sm text-slate-400">Nada pendiente. Todo al día.</li>}
      </ul>
    </section>
  );
}

/** Banda 4 — la foto de un cliente: sell-in, sell-out, riesgo y frescura. */
function BloqueCliente({ k }: { k: KpiCliente }) {
  const base = `/${k.cliente.codigo.toLowerCase()}`;
  const fuente = k.fuente_sellin === 'erp' ? 'facturación APPROX' : k.fuente_sellin === 'pedidos' ? 'pedidos registrados' : 'sin datos';
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display text-base font-extrabold text-slate-900">{k.cliente.codigo}</h2>
          <span className="text-xs text-slate-500">{fuente}</span>
        </div>
        <Link to={`${base}/analisis`} className="text-xs font-semibold text-accent-600 hover:underline">Ver análisis →</Link>
      </header>

      <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100 lg:grid-cols-4">
        <Kpi label={k.sellin ? `Sell-in ${k.sellin.periodo}` : 'Sell-in'}
          valor={k.sellin ? fmt.eur(k.sellin.eur) : '—'}
          pie={k.sellin ? <>vs {k.sellin.comparado_con}: {fmt.eur(k.sellin.eur_py)} · <Delta v={k.sellin.delta_eur} /></> : 'sin ventas registradas'} />
        <Kpi label="Unidades" valor={k.sellin ? fmt.int(k.sellin.uds) : '—'}
          pie={k.sellin ? <>PVP medio {fmt.eur(k.sellin.asp)} · <Delta v={k.sellin.delta_asp} /></> : '—'} />
        <Kpi label={k.sellout ? `Sell-out ${k.sellout.etiqueta}` : 'Sell-out'}
          valor={k.sellout ? `${fmt.int(k.sellout.uds)} uds` : '—'}
          pie={k.sellout
            ? <>{k.sellout.uds_py != null ? <>vs año anterior: <Delta v={k.sellout.delta} /></> : 'sin año anterior'}{k.sellout_en_curso ? ` · ${k.sellout_en_curso.etiqueta} en curso: ${fmt.int(k.sellout_en_curso.uds)}` : ''}</>
            : 'sin sell-out importado'} />
        <Kpi label="Peligro de rotura" alerta={k.rotura.n > 0}
          valor={<Link to={`${base}/productos?productos-${k.cliente.codigo}_f_riesgo_rotura=${k.rotura.umbral}`} className="hover:underline">{fmt.int(k.rotura.n)}</Link>}
          pie={`menos de ${k.rotura.umbral} ${k.rotura.unidad} de cobertura${k.rotura.sin_stock ? ` · ${k.rotura.sin_stock} sin stock` : ''}`} />
      </div>

      <div className="px-5 py-4">
        {k.serie12.length > 1 ? <Serie12 serie={k.serie12} /> : <p className="text-sm text-slate-400">Sin serie de ventas.</p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <Frescura label="Stock" fecha={k.cobertura.stock_hasta} />
          <Frescura label="Sell-out" fecha={k.cobertura.sellout_hasta} />
          <Frescura label="Ventas APPROX" fecha={k.cobertura.ventas_approx_hasta} />
          {k.cobertura.coste_entrada_hasta && <Frescura label="Coste entrada" fecha={k.cobertura.coste_entrada_hasta} />}
        </div>
        {k.top_producto?.sku && (
          <p className="mt-2 truncate text-xs text-slate-500">
            Lo que más factura: <span className="font-mono text-slate-700">{k.top_producto.sku}</span> {k.top_producto.nombre} · {fmt.int(k.top_producto.uds)} uds · {fmt.eur(k.top_producto.eur)}
          </p>
        )}
      </div>
    </section>
  );
}

function Kpi({ label, valor, pie, alerta }: { label: string; valor: ReactNode; pie?: ReactNode; alerta?: boolean }) {
  return (
    <div className="px-5 py-3">
      <div className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500" title={label}>{label}</div>
      <div className={`mt-0.5 font-display text-xl font-extrabold tabular-nums ${alerta ? 'text-warn-600' : 'text-slate-900'}`}>{valor}</div>
      {pie && <div className="mt-0.5 truncate text-xs text-slate-500">{pie}</div>}
    </div>
  );
}

/** Barras de sell-in de 12 meses con la línea de sell-out encima. */
function Serie12({ serie }: { serie: KpiCliente['serie12'] }) {
  const W = 640, H = 90, pad = 6;
  const maxEur = Math.max(1, ...serie.map((s) => s.eur));
  const so = serie.map((s) => s.so_uds).filter((v): v is number => v != null);
  const maxSo = Math.max(1, ...so);
  const bw = (W - pad * 2) / serie.length;
  const y = (v: number, max: number) => H - pad - (v / max) * (H - pad * 2);
  const puntosSo = serie.map((s, i) => (s.so_uds == null ? null : `${(pad + bw * i + bw / 2).toFixed(1)},${y(s.so_uds, maxSo).toFixed(1)}`)).filter(Boolean).join(' ');
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-24 w-full" role="img" aria-label="Sell-in mensual de los últimos 12 meses con el sell-out superpuesto">
        {serie.map((s, i) => (
          <rect key={s.mes} x={pad + bw * i + bw * 0.15} width={bw * 0.7} y={y(s.eur, maxEur)} height={Math.max(0, H - pad - y(s.eur, maxEur))} fill="#A9BFEA" />
        ))}
        {so.length > 1 && <polyline points={puntosSo} fill="none" stroke="#D08A17" strokeWidth="2" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="flex justify-between text-[11px] text-slate-500">
        <span>{serie[0].mes.slice(5)}/{serie[0].mes.slice(2, 4)}</span>
        <span className="flex gap-3">
          <span><span className="inline-block h-2 w-2 rounded-sm bg-accent-300" /> sell-in €</span>
          {so.length > 1 && <span><span className="inline-block h-2 w-2 rounded-sm bg-warn-500" /> sell-out uds</span>}
        </span>
        <span>{serie[serie.length - 1].mes.slice(5)}/{serie[serie.length - 1].mes.slice(2, 4)}</span>
      </div>
    </div>
  );
}

function Frescura({ label, fecha }: { label: string; fecha: string | null }) {
  const dias = useMemo(() => {
    if (!fecha) return null;
    const t = new Date(fecha).getTime();
    return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 86_400_000);
  }, [fecha]);
  return (
    <span title={fecha ? `Último dato: ${fmt.date(fecha)}` : 'Nunca importado'}>
      <span className="text-slate-400">{label}:</span>{' '}
      <span className={dias == null ? 'text-slate-400' : dias > 45 ? 'font-semibold text-warn-600' : 'text-slate-600'}>
        {fecha ? fmt.date(fecha) : 'sin datos'}
      </span>
    </span>
  );
}
